import { useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Close, Edit } from '@mui/icons-material';
import { angularInjectorProvider } from 'Injector';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import { showToast } from 'Toast';
import { messageFrom } from './serverErrors';
import { streamEditorApi } from './streamEditorApi';
import { duplicateKeyTerms, keyTermsForReview, sortKeyTerms } from './keyTerms';
import { type EmbeddedLesson, type KeyTermForReview, type StreamChapter } from './StreamEditor.types';
import { OpenInNewTabButton } from 'ContentItemEditor';

const GENERIC_ERROR = 'Something went wrong. The definition was not saved.';

/*
    A row is one term of one lesson, and the same term in two lessons is exactly what this dialog
    exists to surface. Keyed by the term alone, a definition saved against one lesson appeared on
    every lesson that shares the term, and opening one of those rows seeded the draft with the other
    lesson's text.
*/
function rowKey({ lessonId, rawKeyTerm }: { lessonId: string; rawKeyTerm: string }) {
    return `${lessonId}-${rawKeyTerm}`;
}

// A failure the server reports about itself, the session or the request is not a bug in this code.
const STATUSES_NOT_WORTH_REPORTING = [0, -1, 401, 406, 409, 502, 503, 504];

// A missing definition row is worth knowing about, but it is not an error in this page.
const STATUS_WORTH_A_WARNING = 404;

type FormatsText = { stripFormatting: (text: string) => string };

type Props = {
    open: boolean;
    chapters: StreamChapter[];
    lessonsById: Record<string, EmbeddedLesson>;
    editorUrlForLesson: (lessonId: string) => string;
    onClose: () => void;
};

function reportFailure(error: unknown, { lessonId, keyTerm }: { lessonId: string; keyTerm: string }) {
    const status = (error as { status?: number })?.status;
    if (status !== undefined && STATUSES_NOT_WORTH_REPORTING.includes(status)) return;

    const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
    ErrorLogService?.notify('Failed to save key term definition', error, {
        level: status === STATUS_WORTH_A_WARNING ? 'warning' : 'error',
        lesson_id: lessonId,
        key_term: keyTerm,
        error_status: status,
    });
}

function KeyTermRow({
    term,
    duplicate,
    editorUrlForLesson,
    onEdit,
}: {
    term: KeyTermForReview;
    duplicate: boolean;
    editorUrlForLesson: (lessonId: string) => string;
    onEdit: () => void;
}) {
    return (
        <Stack spacing={0} sx={{ paddingBlock: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography sx={{ fontWeight: duplicate ? 'bold' : undefined }}>{term.keyTerm}</Typography>

                <Typography variant="body2" sx={{ flexGrow: 1, color: 'text.secondary' }}>
                    {`Chapter ${term.chapterIndex + 1}, Lesson ${term.lessonIndex + 1}`}
                </Typography>

                {!!term.definition && (
                    <IconButton aria-label={`Edit the definition of ${term.keyTerm}`} size="small" onClick={onEdit}>
                        <Edit fontSize="small" />
                    </IconButton>
                )}

                <OpenInNewTabButton
                    href={editorUrlForLesson(term.lessonId)}
                    label={`Open ${term.lessonTitle} in the editor`}
                />
            </Stack>

            {!!term.definition && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {term.definition}
                </Typography>
            )}
        </Stack>
    );
}

/*
    Every key term of the course in one list, replacing the Angular review-key-terms-modal.

    A definition saves on its own request and does not touch the course, so editing one here never
    makes the form dirty. The Angular modal had to store its edits under $$-prefixed keys to keep
    stream.asJson() unchanged and stop the unsaved-changes warning from firing; React holds the
    edits outside the form, so there is nothing to hide.
*/
// oxlint-disable-next-line max-lines-per-function -- a list with one inline editor
export default function ReviewKeyTermsDialog({ open, chapters, lessonsById, editorUrlForLesson, onClose }: Props) {
    const [saveKeyTermDefinition] = streamEditorApi.useSaveKeyTermDefinitionMutation();
    const [editing, setEditing] = useState<KeyTermForReview | null>(null);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Definitions saved in this dialog, by lesson and term, so the list reflects an edit without a reload.
    const [savedDefinitions, setSavedDefinitions] = useState<Record<string, string>>({});

    const terms = useMemo(() => {
        // Nothing to build until the admin opens the dialog, and walking every lesson of a large
        // course is not free.
        if (!open) return [];

        const formatsText = angularInjectorProvider.safeGet<FormatsText>('FormatsText');

        // Called through the service, not as a loose reference: stripFormatting reads `this` to
        // reach findMathjaxAndReplace.
        const stripFormatting = (text: string) => formatsText?.stripFormatting(text) ?? text;

        return sortKeyTerms(keyTermsForReview({ chapters, lessonsById, stripFormatting }));
    }, [open, chapters, lessonsById]);

    const duplicates = useMemo(() => duplicateKeyTerms(terms), [terms]);

    async function save() {
        if (!editing) return;

        setSaving(true);
        setErrorMessage(null);

        try {
            const saved = await saveKeyTermDefinition({
                lessonId: editing.lessonId,
                keyTerm: editing.rawKeyTerm,
                definition: draft.trim(),
            }).unwrap();

            setSavedDefinitions(current => ({ ...current, [rowKey(editing)]: saved.definition }));
            setEditing(null);
            showToast({ message: 'Definition saved.', type: 'success' });
        } catch (error) {
            setErrorMessage(messageFrom(error, GENERIC_ERROR));
            reportFailure(error, { lessonId: editing.lessonId, keyTerm: editing.rawKeyTerm });
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            {/* The list runs long, so a close control at the top saves a scroll back down. The
                dialog already closes on the backdrop and on Escape; this makes it visible. */}
            <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                    Review Key Terms
                </Typography>

                <IconButton aria-label="Close the key terms review" onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2">
                    Use the list of key terms below to review. Click the pencil beside a definition to edit it, or the
                    link to open the lesson in a new tab.
                </Typography>

                {duplicates.length > 0 && (
                    <Typography variant="body2" sx={{ marginTop: 2 }}>
                        {`Auto-detected duplicates (exact matches only; be sure to look at complete list): ${duplicates.join(', ')}`}
                    </Typography>
                )}

                <Stack divider={<Divider />}>
                    {terms.map(term => {
                        const definition = savedDefinitions[rowKey(term)] ?? term.definition;
                        const withSavedDefinition = { ...term, definition };
                        const isEditing =
                            editing?.rawKeyTerm === term.rawKeyTerm && editing?.lessonId === term.lessonId;

                        if (!isEditing) {
                            return (
                                <KeyTermRow
                                    key={rowKey(term)}
                                    term={withSavedDefinition}
                                    duplicate={duplicates.includes(term.keyTerm.toLowerCase())}
                                    editorUrlForLesson={editorUrlForLesson}
                                    onEdit={() => {
                                        setEditing(term);
                                        setDraft(definition || '');
                                        setErrorMessage(null);
                                    }}
                                />
                            );
                        }

                        return (
                            <Stack key={`${term.lessonId}-${term.rawKeyTerm}`} spacing={1} sx={{ paddingBlock: 1 }}>
                                <Typography sx={{ fontWeight: 'bold' }}>{term.keyTerm}</Typography>

                                <TextField
                                    multiline
                                    rows={3}
                                    fullWidth
                                    value={draft}
                                    disabled={saving}
                                    onChange={event => setDraft(event.target.value)}
                                />

                                {!!errorMessage && (
                                    <Typography variant="body2" color="error.main">
                                        {errorMessage}
                                    </Typography>
                                )}

                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={saving || !draft.trim()}
                                        onClick={save}
                                    >
                                        Save
                                    </Button>
                                    <Button disabled={saving} onClick={() => setEditing(null)}>
                                        Cancel
                                    </Button>
                                </Stack>
                            </Stack>
                        );
                    })}
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
